import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "JavaScript promises are just like monads and I can explain both in less than 2 minutes",
  "description": "",
  "published": "2017-09-21T00:00:00.000Z",
  "redirect_from": ["/blog/javascript-promises-monads/swizec/7814"],
  "categories": "Learning, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "KVYm0qC4b1Y",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`There's a joke in functional programming that once you understand monads, it becomes impossible to explain them to others. The `}<a parentName="p" {...{
        "href": "https://wiki.haskell.org/Monad_tutorials_timeline"
      }}>{`amount of monad tutorials on the internet`}</a>{` is growing almost exponentially 😁`}</p>
    <p><a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Monad_(functional_programming)"
      }}>{`Monads`}</a>{` are a funky concept that's nearly impossible to understand in all of its nuance. Maybe I'm just not smart enough. That's why I'm not going to explain any of that.`}</p>
    <p>{`Screw the mathematical definition. Look at this shit 👇`}</p>
    <p><img parentName="p" {...{
        "src": "/725ed0f590869fbaca2adbab8818fc6e/continuation-monad26d69b.svg",
        "alt": "continuation monad"
      }}></img></p>
    <p>{`o.O wat O.o`}</p>
    <p>{`Here's a handwavy explanation instead: Monads are like a bubble. They wrap your dirty values and protect the rest of your code from weird effects.`}</p>
    <p>{`I used the continuation monad as an example because it is similar to JavaScript Promises. A way to talk about the future. `}<a parentName="p" {...{
        "href": "#fn1"
      }}>{`1`}</a></p>
    <p>{`Let's pretend you're a `}<a parentName="p" {...{
        "href": "http://hitchhikers.wikia.com/wiki/Mice"
      }}>{`mouse looking for the ultimate question to life, the universe, and everything`}</a>{`. You build a super computer that will calculate the answer, and you call it Earth.`}</p>
    <p>{`You know Earth will take around 4.54 billion years to calculate the question. But you're writing the code right now. You can't wait 4.54 billion years to finish your project.`}</p>
    <p>{`What do you do? You put Earth in a time bubble.`}</p>
    <p>{`Like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`function getQuestion() {
return Earth()
.then(ArthurDent => ArthurDent.subconscious())
.then(subconscious => Scrabble.output(subconscious))
}
`}</code></pre>
    <p>{`In `}<a parentName="p" {...{
        "href": "http://hitchhikers.wikia.com/wiki/Ultimate_Question"
      }}>{`The HitchHiker's Guide to The Galaxy`}</a>{`, Arthur Dent was the final result of a 4.54 billion year calculation. He mindlessly picked letters out of a pile of Scrabble™ tiles, and the result produced the ultimate question.`}</p>
    <p>{`That means our `}<inlineCode parentName="p">{`getQuestion`}</inlineCode>{` function first constructs an Earth, then gets `}<inlineCode parentName="p">{`ArthurDent`}</inlineCode>{`, accesses his subconsciousness, and uses Scrabble™ to print the result. 🤙`}</p>
    <h2 {...{
      "id": "heres-what-that-looks-like-in-practice"
    }}>{`Here's what that looks like in practice:`}</h2>
    <p>{`Let's compare promises to callbacks. You're likely to have met with callbacks before, JavaScript is full of them.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`let theFuture = function (callback) {
  setTimeout(callback, 5000);
};

theFuture(() => {
  console.log("It is now 5 seconds later");
});
`}</code></pre>
    <p>{`We turn `}<inlineCode parentName="p">{`theFuture`}</inlineCode>{` into a Promise and trigger its `}<inlineCode parentName="p">{`resolve`}</inlineCode>{` method after 5 seconds. The `}<inlineCode parentName="p">{`Promise`}</inlineCode>{` wrapped everything inside its body into a protective time bubble.`}</p>
    <p>{`With a promise, we can pass `}<inlineCode parentName="p">{`theFuture`}</inlineCode>{` around and do all sorts of stuff. But if we want to access the future, we have to use `}<inlineCode parentName="p">{`.then`}</inlineCode>{` and give it a function.`}</p>
    <p>{`When I first saw this, I thought `}<em parentName="p">{`"So what's the big deal? This is the same as callbacks.”`}</em></p>
    <p>{`So here's the big deal: Once you're in a promise, you're always in a promise. Because promises are like monads 😁`}</p>
    <p>{`Check out this simplified real world example from my day job.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`makePurchase(){
    this.paymentView
          .fetchPaymentInfo()
          .then(paymentInfo  => this.finishPurchase(paymentInfo))
      .then(token => this.showSuccessModal())
      .catch(error => console.error(error));
}

fetchPaymentInfo(){
    if (this.paypal) {
        return this.getPaypalPaymentInfo(); // returns promise
    }else{
        return this.getCCPaymentInfo(); // returns promise
    }
}

getPaypalPaymentInfo(){
    return this.brainTreeClient
                         .tokenizePaypal()
                         .then(response => {
                 return response.paymentInfo;
                         });
}

finishPurchase( paymentInfo ) {
    return fetch('/purchase/path')
                        .then(response => response.json())
                        .then(json => {
                            if (json.token) {
                                return json.token;
                            }else{
                                throw new Error("Purchase failed")
                            }
                        });
}
`}</code></pre>
    <p>{`Don't worry about the behind-the-scenes details of that code. Here's what you should focus on:`}</p>
    <ol>
      <li parentName="ol">{`The main `}<inlineCode parentName="li">{`makePurchase`}</inlineCode>{` function does everything through Promise access. The `}<inlineCode parentName="li">{`.then`}</inlineCode>{` and `}<inlineCode parentName="li">{`.catch`}</inlineCode>{` methods are like peeking into the Promise time bubble. You're saying: Once this time bubble resolves, I want to do so and so with the result.`}</li>
      <li parentName="ol">{`The `}<inlineCode parentName="li">{`fetchPaymentInfo`}</inlineCode>{` is the first method in our chain that creates a Promise. From then onwards, we can access returned values only through `}<inlineCode parentName="li">{`.then`}</inlineCode>{` and `}<inlineCode parentName="li">{`.catch`}</inlineCode>{`. It uses `}<inlineCode parentName="li">{`getPaypalPaymentInfo`}</inlineCode>{` and `}<inlineCode parentName="li">{`getCCPaymentInfo`}</inlineCode>{` to talk to Braintree, which is an operation that takes some time.`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`braintreeClient.tokenizePaypal()`}</inlineCode>{` returns a promise. There's no need to wrap this in another Promise inside `}<inlineCode parentName="li">{`fetchPaymentMethod`}</inlineCode>{`. You can return it like any normal value.`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`getPaypalPaymentInfo`}</inlineCode>{` uses `}<inlineCode parentName="li">{`.then`}</inlineCode>{` to look into the Braintree response. Since you're in a Promise, you can `}<inlineCode parentName="li">{`return`}</inlineCode>{` flat values without worry. They're already wrapped in a Promise.`}</li>
      <li parentName="ol">{`As a result, we can chain multiple `}<inlineCode parentName="li">{`.then`}</inlineCode>{` calls in `}<inlineCode parentName="li">{`makePurchase`}</inlineCode>{`. Some methods return a regular value, some return a Promise. JavaScript don't care, it's all the same `}<em parentName="li">{`because everything returned from a Promise is a Promise`}</em>{`.`}</li>
    </ol>
    <p>{`And that's why Promises are just like monads.`}</p>
    <p>{`Oh, and error handling. Don't worry about that either. As long as there's a `}<inlineCode parentName="p">{`.catch`}</inlineCode>{` call somewhere in the chain, you're good. Errors bubble up through the chain of Promise look-into-s until they encounter a `}<inlineCode parentName="p">{`.catch`}</inlineCode>{`.`}</p>
    <p>{`Hope that helps, it took me months of practice to grok 🤓`}</p>
    <ol>
      <li parentName="ol">{`Technically continuations are more similar to callbacks, but bear with me. `}<a parentName="li" {...{
          "href": "#ffn1"
        }}>{`↩`}</a></li>
    </ol>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      